import { AddressOverrideConfirmDialogComponent } from './address-override-confirm-dialog/address-override-confirm-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { DeleteConfirmDialogComponent } from './delete-confirm-dialog/delete-confirm-dialog.component';
import { FormGroup } from '@angular/forms';
import { IAddressValidationResponse } from './common-interfaces';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ICountry } from './components/address/country';
import { IState } from './country-states-service';
import { ListViewComponent } from './list-view/list-view.component';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { ComponentType } from '@angular/cdk/portal';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Injectable({
  providedIn: 'root',
})
export class ConfirmDialogService {

  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
  }

  openAddressOverrideConfirmDialog(
    addressValidationResponse: IAddressValidationResponse, states: IState[], countries: ICountry[]): Observable<boolean> {
    return this.dialog.open(AddressOverrideConfirmDialogComponent, {
      disableClose: true,
      position: { top: '100px' },
      data: {
        addressValidationResponse,
        states,
        countries,
      },
    }).afterClosed();
  }

  confirmCancelInModal(form: FormGroup, modalDialogRef: MatDialogRef<any>) {
    if (form.dirty) {
      this.openConfirmDialog(
        this.translateService.stream(marker('Any unsaved changes will be lost.')),
      ).subscribe(response => {
        if (response) {
          modalDialogRef.close();
        }
      });
    } else {
      modalDialogRef.close();
    }
  }

  confirmCancelWithFn(form: FormGroup, performFn: any) {
    if (form.dirty) {
      this.openConfirmDialog(
        this.translateService.stream(marker('Any unsaved changes will be lost.')),
      ).subscribe(response => {
        if (response) {
          performFn();
        }
      });
    } else {
      performFn();
    }
  }

  confirmCancelNoCheck(performFn: any) {
    this.openConfirmDialog(
      this.translateService.stream(marker('Any unsaved changes will be lost.')),
    ).subscribe(response => {
      if (response) {
        performFn();
      }
    });
  }

  openConfirmDialog(message: Observable<string>,
    messagesList: Observable<string>[] = [],
    labels: {yesLabel: Observable<string>, noLabel: Observable<string>} =
    {
    yesLabel: this.translateService.stream(marker('Ok')),
    noLabel: this.translateService.stream(marker('No')),
    },
    ): Observable<boolean> {

    return this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      position: { top: '100px' },
      data: {
        message,
        messagesList,
        labels: labels,
      },
    }).afterClosed();
  }

  openDeleteConfirmDialog(itemName?: string, message2?: Observable<string>) {
    const message = itemName ? this.translateService.stream(marker('Are you sure you want to delete')) :
      this.translateService.stream(marker('Are you sure you want to delete this item'));

    return this.dialog.open(DeleteConfirmDialogComponent, {
      disableClose: true,
      position: { top: '100px' },
      data: {
        message,
        itemName,
        message2,
      },
    });
  }

  deleteListItem(itemName: string,
                 listView: ListViewComponent<any>,
                 deleteOperation: Observable<any>,
                 successMessage: Observable<string>,
                 takeUntil$?: Subject<any>) {
    this.openDeleteConfirmDialog(itemName)
      .afterClosed()
      .pipe(takeUntil(takeUntil$))
      .subscribe(res => {
        if (res) {
          deleteOperation
            .pipe(takeUntil(takeUntil$))
            .subscribe((resp) => {
              if (resp === null) {
                this.notificationService.success(successMessage);
                listView.loadItems(true);
              }
            });
        }
      });
  }

  addListItem(
    component: ComponentType<any>,
    listView: ListViewComponent<any>,
    takeUntil$?: Subject<any>) {

    const isMobile = this.breakpointObserver.observe(
      Breakpoints.Handset,
    ).pipe(
      takeUntil(takeUntil$),
    );

    const dialogRef = this.dialog.open(component, {
      disableClose: true,
      autoFocus: true,
      panelClass: 'dialog--no-padding',
      maxWidth: '100vw',
      maxHeight: '90vh',
    });
    const isMobileSubscription = isMobile.subscribe(size => {
      if (size.matches) {
        dialogRef.updateSize('95vw');
      } else {
        dialogRef.updateSize('fit-content');
      }
    });

    dialogRef.componentInstance.saveEv
      .pipe(takeUntil(takeUntil$))
      .subscribe((data) => {
        listView.loadItems(true);
      });

    dialogRef.afterClosed()
      .pipe(takeUntil(takeUntil$))
      .subscribe(() => {
        isMobileSubscription.unsubscribe();
        listView.loadItems(true);
      });
  }

}
